/**
 * Cache Manager — orchestrator for the player offline caching system.
 *
 * Responsibilities:
 * - Download assets and store them in the appropriate backend (Cache Storage or OPFS)
 * - Validate SHA-256 checksums after download
 * - Store schedule manifests in IndexedDB with 24h TTL
 * - Pre-download all assets referenced in the manifest for the next 24h
 * - Eviction: remove expired campaign assets first, then LRU; maintain 20% disk headroom
 * - Never evict assets needed in next 24h or fallback playlist assets
 */

import type { ScheduleManifest } from '@dooh/shared';
import { validateChecksum } from './checksum';
import { cacheAsset, getCachedAsset, getCachedAssetBlobUrl, deleteCachedAsset, getStorageEstimate } from './cache-storage';
import { storeManifest, loadManifest, loadAssetMeta, loadAllAssetMeta, storeAssetMeta, deleteAssetMeta, touchAssetLastPlayed } from './indexeddb';
import { readOpfsFile, deleteOpfsFile, existsOpfsFile, listOpfsFiles, downloadOpfsFile, getOpfsFile } from './opfs-client';
import {
  OPFS_SIZE_THRESHOLD,
  DISK_HEADROOM_PERCENT,
  type CachedAssetMeta,
  type AssetDownloadInfo,
  type DownloadResult,
  type StorageQuotaInfo,
  type PreDownloadStats,
} from './types';

/** Logger prefix */
const LOG_PREFIX = '[CacheManager]';

function log(...args: unknown[]) {
  console.log(LOG_PREFIX, ...args);
}

function warn(...args: unknown[]) {
  console.warn(LOG_PREFIX, ...args);
}

/**
 * Extract all asset download info from a schedule manifest.
 * Returns unique assets with their campaign associations.
 */
export function extractAssetInfoFromManifest(
  manifest: ScheduleManifest,
  assetUrlMap: Map<string, { url: string; checksum: string; fileSize: number; type: 'image' | 'video'; duration?: number | null }>,
  fallbackAssetIds: string[],
): AssetDownloadInfo[] {
  const assetMap = new Map<string, AssetDownloadInfo>();

  for (const slot of manifest.slots) {
    for (const assetId of slot.assetIds) {
      if (assetMap.has(assetId)) {
        // Add campaign associations
        const existing = assetMap.get(assetId)!;
        if (!existing.campaignIds.includes(slot.campaignId)) {
          existing.campaignIds.push(slot.campaignId);
        }
        if (!existing.campaignEndDates.includes(slot.endDate)) {
          existing.campaignEndDates.push(slot.endDate);
        }
        continue;
      }

      const assetInfo = assetUrlMap.get(assetId);
      if (!assetInfo) {
        warn(`Asset ${assetId} not found in URL map, skipping`);
        continue;
      }

      assetMap.set(assetId, {
        assetId,
        url: assetInfo.url,
        checksum: assetInfo.checksum,
        fileSize: assetInfo.fileSize,
        assetType: assetInfo.type,
        duration: assetInfo.duration ?? null,
        campaignIds: [slot.campaignId],
        campaignEndDates: [slot.endDate],
        isFallback: fallbackAssetIds.includes(assetId),
      });
    }
  }

  // Also include fallback assets not already covered
  for (const assetId of fallbackAssetIds) {
    if (assetMap.has(assetId)) {
      assetMap.get(assetId)!.isFallback = true;
      continue;
    }
    const assetInfo = assetUrlMap.get(assetId);
    if (!assetInfo) continue;
    assetMap.set(assetId, {
      assetId,
      url: assetInfo.url,
      checksum: assetInfo.checksum,
      fileSize: assetInfo.fileSize,
      assetType: assetInfo.type,
      duration: assetInfo.duration ?? null,
      campaignIds: [],
      campaignEndDates: [],
      isFallback: true,
    });
  }

  return Array.from(assetMap.values());
}

/**
 * Parse a campaign end date as INCLUSIVE end-of-day (#191).
 *
 * Slot/campaign end dates are date-only (`YYYY-MM-DD`) and represent the WHOLE
 * final day: `schedule-evaluator.isSlotActive` counts a slot active through the
 * end of `endDate` (string comparison `currentDate > endDate`). Parsing a bare
 * date with `new Date('2025-06-16')` yields 00:00 UTC — the START of the day —
 * so eviction would treat an asset as expired/unneeded at midnight on its final
 * campaign day, evicting content that is still scheduled and playing.
 *
 * Treating a date-only value as 23:59:59.999 UTC keeps the eviction window in
 * agreement with playback. Values that already carry a time component are
 * parsed as-is.
 */
export function parseInclusiveEndDate(dateStr: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(`${dateStr}T23:59:59.999Z`);
  }
  return new Date(dateStr);
}

/**
 * Determine if an asset is needed within the next 24 hours based on manifest slots.
 */
export function isAssetNeededInNext24h(
  assetId: string,
  manifest: ScheduleManifest,
): boolean {
  const now = new Date();
  const futureDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  for (const slot of manifest.slots) {
    if (!slot.assetIds.includes(assetId)) continue;

    // endDate is inclusive end-of-day so an asset on its final campaign day is
    // still considered needed (#191). startDate stays at 00:00 (start of day).
    const slotEnd = parseInclusiveEndDate(slot.endDate);
    const slotStart = new Date(slot.startDate);

    // If the slot's date range includes the next 24h window
    if (slotStart <= futureDate && slotEnd >= now) {
      return true;
    }
  }

  return false;
}

/**
 * Download and cache a single asset.
 */
export async function downloadAndCacheAsset(
  info: AssetDownloadInfo,
): Promise<DownloadResult> {
  const { assetId, url, checksum, fileSize, assetType } = info;

  try {
    // Choose storage backend based on file size
    const useOpfs = fileSize >= OPFS_SIZE_THRESHOLD;

    if (useOpfs) {
      // Check if already in OPFS
      const exists = await existsOpfsFile(assetId);
      if (exists.exists) {
        // Don't trust the OPFS hit blindly (#192). Mirror the #38 Cache-Storage
        // fix: an asset re-uploaded under the same ID keeps the OPFS filename
        // but changes content, so compare the stored metadata's expected
        // checksum to the manifest's expected checksum. Only skip the download
        // when they match AND the stored copy was previously verified;
        // otherwise the on-disk bytes are (or may be) stale — re-download and
        // re-verify instead of marking the new checksum as verified.
        const storedMeta = await loadAssetMeta(assetId);
        if (
          storedMeta &&
          storedMeta.checksumVerified &&
          storedMeta.expectedChecksum === checksum
        ) {
          log(`Asset ${assetId} already in OPFS, checksum matches — skipping download`);
          await saveAssetMeta(info, 'opfs', true);
          return {
            assetId,
            success: true,
            storageBackend: 'opfs',
            checksumVerified: true,
          };
        }
        warn(
          `OPFS asset ${assetId} present but checksum unverified/changed ` +
            `(stored=${storedMeta?.expectedChecksum ?? 'none'}, expected=${checksum}) — re-downloading`,
        );
        // Fall through to re-download + re-verify below.
      }

      // Stream the download into OPFS inside the worker, hashing chunks
      // incrementally. The previous path buffered the entire file THREE
      // times (response.arrayBuffer → crypto.subtle input → structured
      // clone into the worker); on the 2GB Pi 5 fleet target that could
      // OOM the renderer during background pre-downloads. Peak memory is
      // now one network chunk regardless of asset size.
      const result = await downloadOpfsFile(assetId, url, checksum);
      if (!result.success) {
        if (result.error === 'SHA-256 checksum mismatch') {
          warn(`Checksum mismatch for ${assetId}`);
        }
        return {
          assetId,
          success: false,
          storageBackend: 'opfs',
          checksumVerified: false,
          error: result.error,
        };
      }

      // Save metadata
      await saveAssetMeta(info, 'opfs', true);

      log(`Cached ${assetId} in OPFS (${fileSize} bytes, checksum ✓)`);
      return {
        assetId,
        success: true,
        storageBackend: 'opfs',
        checksumVerified: true,
      };
    } else {
      // Cache Storage path
      const alreadyCached = await getCachedAsset(url);
      if (alreadyCached) {
        // Recompute checksum on cache hit (#38) — don't trust the cached
        // entry blindly. A partial/corrupt entry from an interrupted write,
        // or a poisoned Cache Storage entry, would otherwise be served forever.
        const cachedData = await alreadyCached.arrayBuffer();
        const cachedChecksumValid = await validateChecksum(cachedData, checksum);
        if (!cachedChecksumValid) {
          warn(`Cached asset ${assetId} failed checksum revalidation — re-downloading`);
          // Evict the corrupt entry and fall through to re-download
          await deleteCachedAsset(url);
        } else {
          log(`Asset ${assetId} in Cache Storage, checksum revalidated ✓`);
          await saveAssetMeta(info, 'cache-storage', true);
          return {
            assetId,
            success: true,
            storageBackend: 'cache-storage',
            checksumVerified: true,
          };
        }
      }

      // Download
      const response = await fetch(url);
      if (!response.ok) {
        return {
          assetId,
          success: false,
          storageBackend: 'cache-storage',
          checksumVerified: false,
          error: `HTTP ${response.status}`,
        };
      }

      const data = await response.arrayBuffer();

      // Validate checksum
      const checksumValid = await validateChecksum(data, checksum);
      if (!checksumValid) {
        warn(`Checksum mismatch for ${assetId}`);
        return {
          assetId,
          success: false,
          storageBackend: 'cache-storage',
          checksumVerified: false,
          error: 'SHA-256 checksum mismatch',
        };
      }

      // Store in Cache Storage
      const cachedResponse = new Response(data, {
        headers: {
          'Content-Type': assetType === 'video' ? 'video/mp4' : 'image/jpeg',
          'Content-Length': String(fileSize),
        },
      });
      await cacheAsset(url, cachedResponse);

      // Save metadata
      await saveAssetMeta(info, 'cache-storage', checksumValid);

      log(`Cached ${assetId} in Cache Storage (${fileSize} bytes, checksum ✓)`);
      return {
        assetId,
        success: true,
        storageBackend: 'cache-storage',
        checksumVerified: true,
      };
    }
  } catch (err) {
    warn(`Failed to download ${assetId}:`, err);
    return {
      assetId,
      success: false,
      storageBackend: fileSize >= OPFS_SIZE_THRESHOLD ? 'opfs' : 'cache-storage',
      checksumVerified: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Save asset metadata to IndexedDB.
 */
async function saveAssetMeta(
  info: AssetDownloadInfo,
  backend: 'cache-storage' | 'opfs',
  checksumVerified: boolean,
): Promise<void> {
  const meta: CachedAssetMeta = {
    assetId: info.assetId,
    url: info.url,
    expectedChecksum: info.checksum,
    checksumVerified,
    fileSize: info.fileSize,
    assetType: info.assetType,
    duration: info.duration ?? null,
    storageBackend: backend,
    campaignIds: info.campaignIds,
    lastPlayedAt: null,
    campaignEndDates: info.campaignEndDates,
    isFallback: info.isFallback,
    cachedAt: Date.now(),
  };
  await storeAssetMeta(meta);
}

/**
 * Get a cached asset's data (ArrayBuffer) from either Cache Storage or OPFS.
 */
export async function getCachedAssetData(
  meta: CachedAssetMeta,
): Promise<ArrayBuffer | null> {
  if (meta.storageBackend === 'opfs') {
    const result = await readOpfsFile(meta.assetId);
    return result.data ?? null;
  } else {
    const response = await getCachedAsset(meta.url);
    if (!response) return null;
    return response.arrayBuffer();
  }
}

/**
 * Get storage quota info and check headroom.
 */
export async function getStorageQuotaInfo(): Promise<StorageQuotaInfo> {
  const { usage, quota } = await getStorageEstimate();
  const headroomTarget = (quota * DISK_HEADROOM_PERCENT) / 100;
  const available = quota - usage;
  return {
    usage,
    quota,
    available,
    headroomTarget,
    isHeadroomMet: available >= headroomTarget,
  };
}

/**
 * The asset currently on screen. Playback now streams straight from the
 * OPFS file on disk (see getAssetPlaybackUrl), so deleting that file would
 * break the in-flight video — the old in-RAM blob was immune to this.
 * PlaybackScreen keeps this current; eviction never touches it. The
 * next-24h manifest rule already protects scheduled content, but emergency
 * assets play outside the manifest and would otherwise be evictable
 * mid-display.
 */
let activeAssetId: string | null = null;

export function markAssetActive(assetId: string | null): void {
  activeAssetId = assetId;
}

/**
 * Evict assets according to LRU + expired policy.
 * Maintains 20% disk headroom.
 *
 * Priority:
 * 1. Never evict the asset currently on screen
 * 2. Never evict fallback playlist assets
 * 3. Never evict assets needed in next 24h
 * 4. Evict expired campaign assets first
 * 5. Then evict least-recently-played assets
 */
export async function runEviction(
  manifest: ScheduleManifest,
): Promise<{ evictedCount: number; freedBytes: number }> {
  const allMeta = await loadAllAssetMeta();
  const quotaInfo = await getStorageQuotaInfo();

  // If headroom is met, no eviction needed
  if (quotaInfo.isHeadroomMet) {
    log('Disk headroom sufficient, no eviction needed');
    return { evictedCount: 0, freedBytes: 0 };
  }

  const targetFree = quotaInfo.headroomTarget;
  const neededFree = targetFree - quotaInfo.available;

  log(`Need to free ${neededFree} bytes to reach ${DISK_HEADROOM_PERCENT}% headroom`);

  // Categorize assets
  const evictable: CachedAssetMeta[] = [];
  const now = new Date();

  for (const meta of allMeta) {
    // Never evict the asset currently on screen (disk-backed playback)
    if (meta.assetId === activeAssetId) continue;

    // Never evict fallback playlist assets
    if (meta.isFallback) continue;

    // Never evict assets needed in next 24h
    if (isAssetNeededInNext24h(meta.assetId, manifest)) continue;

    evictable.push(meta);
  }

  // Sort: expired campaigns first, then by lastPlayedAt (LRU)
  evictable.sort((a, b) => {
    const aExpired = hasExpiredCampaigns(a, now);
    const bExpired = hasExpiredCampaigns(b, now);

    // Expired campaigns come first
    if (aExpired && !bExpired) return -1;
    if (!aExpired && bExpired) return 1;

    // Then by LRU (null = never played → evict first)
    const aTime = a.lastPlayedAt ?? 0;
    const bTime = b.lastPlayedAt ?? 0;
    return aTime - bTime;
  });

  let freedBytes = 0;
  let evictedCount = 0;

  for (const meta of evictable) {
    if (freedBytes >= neededFree) break;

    try {
      // Remove from storage backend. OPFS deletes report failure via a result
      // object rather than throwing (#193b): a failed delete must NOT count
      // toward freedBytes or delete the metadata, otherwise we orphan the file
      // (still on disk but no meta record) AND over-credit the eviction — the
      // headroom target would look met while the bytes are still occupied.
      if (meta.storageBackend === 'opfs') {
        const result = await deleteOpfsFile(meta.assetId);
        if (!result.success) {
          warn(`Failed to evict OPFS asset ${meta.assetId}: ${result.error}`);
          continue;
        }
      } else {
        await deleteCachedAsset(meta.url);
      }

      // Remove metadata only after the file itself was actually removed.
      await deleteAssetMeta(meta.assetId);

      freedBytes += meta.fileSize;
      evictedCount++;
      log(`Evicted ${meta.assetId} (${meta.fileSize} bytes, ${hasExpiredCampaigns(meta, now) ? 'expired' : 'LRU'})`);
    } catch (err) {
      warn(`Failed to evict ${meta.assetId}:`, err);
    }
  }

  log(`Eviction complete: ${evictedCount} assets freed, ${freedBytes} bytes`);
  return { evictedCount, freedBytes };
}

/**
 * Clear ALL OPFS-backed asset files (#193a).
 *
 * The CLEAR_CACHE remote command previously purged Cache Storage + IndexedDB
 * metadata but never touched OPFS, leaving large cached videos orphaned on disk
 * (occupying quota with no metadata pointing at them). This enumerates every
 * OPFS file and deletes it, isolating per-file failures so one bad delete does
 * not abort the rest of the clear.
 */
export async function clearOpfsAssets(): Promise<void> {
  let assetIds: string[];
  try {
    assetIds = await listOpfsFiles();
  } catch (err) {
    warn('Failed to list OPFS files during clear:', err);
    return;
  }

  let cleared = 0;
  for (const assetId of assetIds) {
    try {
      const result = await deleteOpfsFile(assetId);
      if (result.success) {
        cleared++;
      } else {
        warn(`Failed to delete OPFS file ${assetId} during clear: ${result.error}`);
      }
    } catch (err) {
      warn(`Error deleting OPFS file ${assetId} during clear:`, err);
    }
  }

  log(`Cleared ${cleared}/${assetIds.length} OPFS asset(s)`);
}

/**
 * Check if any of an asset's campaigns have expired.
 */
function hasExpiredCampaigns(meta: CachedAssetMeta, now: Date): boolean {
  if (meta.campaignEndDates.length === 0) return false;
  // Compare against inclusive end-of-day so a campaign is only "expired" AFTER
  // its final day has fully passed, matching isSlotActive/playback (#191).
  return meta.campaignEndDates.every((dateStr) => parseInclusiveEndDate(dateStr) < now);
}

/**
 * Pre-download all assets referenced in the schedule manifest for the next 24 hours.
 */
export async function preDownloadAssets(
  manifest: ScheduleManifest,
  assetUrlMap: Map<string, { url: string; checksum: string; fileSize: number; type: 'image' | 'video'; duration?: number | null }>,
  fallbackAssetIds: string[],
): Promise<PreDownloadStats> {
  const assetInfos = extractAssetInfoFromManifest(manifest, assetUrlMap, fallbackAssetIds);

  const stats: PreDownloadStats = {
    totalAssets: assetInfos.length,
    downloaded: 0,
    skipped: 0,
    failed: 0,
    evicted: 0,
    bytesDownloaded: 0,
  };

  log(`Pre-downloading ${assetInfos.length} assets for next 24h`);

  // Run eviction first to free space
  const evictionResult = await runEviction(manifest);
  stats.evicted = evictionResult.evictedCount;

  // Load all cached asset metadata ONCE before the loop (#30) — previously
  // this was a full IndexedDB getAll() per asset (O(n²) reads).
  const allCached = await loadAllAssetMeta();
  const cachedMap = new Map(allCached.map((m) => [m.assetId, m]));

  for (const info of assetInfos) {
    // Check if already cached (from the pre-loaded map)
    const alreadyCached = cachedMap.get(info.assetId);

    if (alreadyCached?.checksumVerified) {
      // Refresh the stored presigned URL even though we skip the download
      // (#173). Presigned S3 URLs expire, but the "already cached + checksum
      // verified → skip" path previously kept the ORIGINAL meta.url forever,
      // so playback could later resolve to an expired URL and fail. When the
      // incoming manifest carries a NEWER presigned URL for this asset, persist
      // it (preserving every other field of the existing record). Only re-save
      // when the URL actually changed to avoid needless IndexedDB writes.
      if (alreadyCached.url !== info.url) {
        const refreshed: CachedAssetMeta = { ...alreadyCached, url: info.url };
        await storeAssetMeta(refreshed);
        cachedMap.set(info.assetId, refreshed);
        log(`Refreshed presigned URL for cached asset ${info.assetId}`);
      }
      stats.skipped++;
      continue;
    }

    const result = await downloadAndCacheAsset(info);
    if (result.success) {
      stats.downloaded++;
      stats.bytesDownloaded += info.fileSize;
    } else {
      stats.failed++;
      warn(`Failed to pre-download ${info.assetId}: ${result.error}`);
    }
  }

  log(
    `Pre-download complete: ${stats.downloaded} downloaded, ${stats.skipped} skipped, ${stats.failed} failed, ${stats.evicted} evicted`,
  );

  return stats;
}

/**
 * Register and cache an emergency asset delivered with an EMERGENCY command (#71).
 *
 * Emergency creative is often NOT in the local cache (fresh device, rotated
 * asset, asset outside the compiled manifest). Waiting for a full download
 * before rendering would delay a safety-critical message, so this:
 *
 * 1. Immediately writes an asset-meta record pointing at the remote URL
 *    (storageBackend 'cache-storage' → getAssetPlaybackUrl returns the URL,
 *    so <img>/<video> can stream it from the CMS right away while online).
 * 2. Downloads + checksum-validates the asset through the normal cache path
 *    in the background so subsequent renders are offline-capable.
 *
 * If the asset is already cached and checksum-verified this is a no-op.
 */
export async function registerAndCacheEmergencyAsset(
  assetId: string,
  entry: { url: string; checksum: string; fileSize: number; type: 'image' | 'video'; duration?: number | null },
  onRegistered?: () => void | Promise<void>,
): Promise<DownloadResult> {
  const info: AssetDownloadInfo = {
    assetId,
    url: entry.url,
    checksum: entry.checksum,
    fileSize: entry.fileSize,
    assetType: entry.type,
    campaignIds: [],
    campaignEndDates: [],
    isFallback: false,
  };

  const existing = await loadAllAssetMeta();
  const cached = existing.find((m) => m.assetId === assetId);
  if (cached?.checksumVerified) {
    log(`Emergency asset ${assetId} already cached and verified`);
    return {
      assetId,
      success: true,
      storageBackend: cached.storageBackend,
      checksumVerified: true,
    };
  }

  // Step 1: provisional meta so PlaybackScreen can resolve the remote URL
  // immediately. checksumVerified=false marks it for re-download by any
  // later pre-download pass.
  await storeAssetMeta({
    assetId,
    url: entry.url,
    expectedChecksum: entry.checksum,
    checksumVerified: false,
    fileSize: entry.fileSize,
    assetType: entry.type,
    storageBackend: 'cache-storage',
    campaignIds: [],
    lastPlayedAt: null,
    campaignEndDates: [],
    isFallback: false,
    cachedAt: Date.now(),
  });

  // Let the caller refresh UI state now — the emergency can render from the
  // remote URL without waiting for the download below. A UI-side throw must
  // not skip the background download that makes replays offline-capable.
  try {
    await onRegistered?.();
  } catch (err) {
    warn(`Emergency asset ${assetId} onRegistered callback failed: ${err}`);
  }

  // Step 2: real download + checksum validation (overwrites the meta record
  // with the correct backend on success).
  const result = await downloadAndCacheAsset(info);
  if (!result.success) {
    warn(`Emergency asset ${assetId} download failed (streaming remotely): ${result.error}`);
  }
  return result;
}

/**
 * Store a schedule manifest and trigger pre-download.
 */
export async function storeManifestAndPreDownload(
  manifest: ScheduleManifest,
  screenId: string,
  assetUrlMap: Map<string, { url: string; checksum: string; fileSize: number; type: 'image' | 'video'; duration?: number | null }>,
  fallbackAssetIds: string[],
): Promise<PreDownloadStats> {
  log(`Storing manifest v${manifest.version} for screen ${screenId}`);

  // Store manifest in IndexedDB
  await storeManifest(manifest, screenId);

  // Pre-download assets
  return preDownloadAssets(manifest, assetUrlMap, fallbackAssetIds);
}

/**
 * Load cached manifest from IndexedDB.
 * Returns null if not found or expired.
 */
export async function loadCachedManifest(
  screenId: string,
): Promise<{ manifest: ScheduleManifest; isExpired: boolean } | null> {
  return loadManifest(screenId);
}

/**
 * Get a cached asset URL for playback.
 * For OPFS assets, returns a blob URL.
 * For Cache Storage assets, returns a blob URL read from the local cache when
 * present, falling back to the remote URL only when nothing is cached.
 */
export async function getAssetPlaybackUrl(
  meta: CachedAssetMeta,
): Promise<string | null> {
  if (meta.storageBackend === 'opfs') {
    // Disk-backed playback (2GB-device target): an OPFS `File` is a Blob
    // whose bytes stay on disk — Chromium's media stack streams it with
    // native range/seek support. The previous path read the whole file
    // into an ArrayBuffer (copied again crossing the worker boundary, and
    // again into a fresh Blob), so every playlist rotation re-buffered
    // entire videos in RAM. Wrapping the File in a typed Blob is a
    // by-reference composite — it re-labels the MIME type without copying.
    const file = await getOpfsFile(meta.assetId);
    if (!file) return null;
    const blob = new Blob([file], {
      type: meta.assetType === 'video' ? 'video/mp4' : 'image/jpeg',
    });
    return URL.createObjectURL(blob);
  } else {
    // Cache Storage: serve the cached bytes directly as a blob URL. The video
    // element must NOT play from the raw presigned URL here — it points at a
    // cross-origin S3 host that the Service Worker does not intercept (it skips
    // cross-origin requests), and the presigned URL's STS token expires long
    // before the manifest window, after which S3 returns 400 ExpiredToken and
    // the <video> fails to load (MediaError SRC_NOT_SUPPORTED). The bytes are
    // already cached locally, so use them and stay independent of URL expiry.
    const blobUrl = await getCachedAssetBlobUrl(meta.url);
    if (blobUrl) return blobUrl;
    // No local copy cached yet — fall back to the remote URL (only briefly
    // valid right after signing; otherwise the item is skipped as before).
    return meta.url;
  }
}

/**
 * Record that an asset was played (updates LRU timestamp).
 */
export async function recordAssetPlayed(assetId: string): Promise<void> {
  await touchAssetLastPlayed(assetId);
}
